import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, RefreshCw, FileText, AlertTriangle, Banknote, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useReservationsVefa, useCreateReservationVefa, useAjouterAcompte,
  useDeleteReservationVefa, useLotsImmo,
} from '@/hooks/useImmobilier';
import { useContacts } from '@/hooks/useCrm';
import {
  STATUT_RESERVATION_VEFA_LABELS,
  type ReservationVefa, type StatutReservationVefa,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutReservationVefa, string> = {
  en_cours:          'ds-bd ds-bd-n',
  signee:            'ds-bd ds-bd-i',
  acomptes_partiels: 'ds-bd ds-bd-w',
  acompte_complete:  'ds-bd ds-bd-s',
  acte_authentique:  'ds-bd ds-bd-s',
  annulee:           'ds-bd ds-bd-d',
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const ReservationsVefaPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]                 = useState<StatutReservationVefa | ''>('');
  const [dialogOpen, setDialogOpen]         = useState(false);
  const [acompteFor, setAcompteFor]         = useState<ReservationVefa | null>(null);
  const [error, setError]                   = useState<string | null>(null);

  const { data, isLoading, refetch } = useReservationsVefa({ statut: statut || undefined, limit: 100 });
  const reservations = data?.data ?? [];
  const remove = useDeleteReservationVefa();

  const onRemove = async (id: string, ref: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer la réservation "${ref}" ?`, message: 'Le lot sera libéré si encore réservé.', confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const totalAcomptes = reservations.filter((r) => r.statut !== 'annulee').reduce((s, r) => s + Number(BigInt(r.acompteCumuleCents)) / 100, 0);
  const enCoursCount = reservations.filter((r) => ['en_cours', 'signee', 'acomptes_partiels', 'acompte_complete'].includes(r.statut)).length;
  const acteCount = reservations.filter((r) => r.statut === 'acte_authentique').length;

  return (
    <>
      <Topbar
        title="Réservations VEFA"
        subtitle={filiale ? `Ventes en État Futur d'Achèvement · ${filiale.fullName}` : 'Réservations VEFA'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}><Plus size={14} /> Nouvelle réservation</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">📝</div><div className="ds-kr-val">{reservations.length}</div><div className="ds-kr-lbl">Réservations</div></div>
          <div className="ds-kr kr-c3"><div className="ds-kr-ico">⏳</div><div className="ds-kr-val">{enCoursCount}</div><div className="ds-kr-lbl">En cours</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">🤝</div><div className="ds-kr-val">{acteCount}</div><div className="ds-kr-lbl">Ventes définitives</div></div>
          <div className="ds-kr kr-c4"><div className="ds-kr-ico">💰</div><div className="ds-kr-val">{formatNumber(totalAcomptes)}</div><div className="ds-kr-lbl">Acomptes cumulés (FCFA)</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutReservationVefa | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_RESERVATION_VEFA_LABELS) as StatutReservationVefa[]).map((s) => <option key={s} value={s}>{STATUT_RESERVATION_VEFA_LABELS[s]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Réservations</div><div className="ds-table-sub">{reservations.length} réservation{reservations.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 130 }}>Référence</th>
              <th>Client</th>
              <th>Lot</th>
              <th className="ds-am" style={{ width: 130 }}>Prix vente</th>
              <th className="ds-am" style={{ width: 130 }}>Acompte cumulé</th>
              <th className="ds-am" style={{ width: 80 }}>% Payé</th>
              <th style={{ width: 100 }}>Date</th>
              <th style={{ width: 130 }}>Statut</th>
              <th style={{ width: 110 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && reservations.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                  <FileText size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune réservation VEFA</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={() => setDialogOpen(true)}><Plus size={12} /> Créer la première réservation</button>
                </td></tr>
              )}
              {!isLoading && reservations.map((r) => {
                const prix = Number(BigInt(r.prixVenteCents)) / 100;
                const acompte = Number(BigInt(r.acompteCumuleCents)) / 100;
                const pct = prix > 0 ? Math.round((acompte / prix) * 100) : 0;
                return (
                  <tr key={r.id}>
                    <td><span className="ds-ref-code">{r.reference}</span></td>
                    <td style={{ fontSize: 12 }}>{r.client?.companyName ?? r.client?.fullName ?? '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      <div className="ds-entry-label">{r.lot?.code ?? '—'}</div>
                      {r.lot?.programme && <div className="ds-entry-sub">{r.lot.programme.code}</div>}
                    </td>
                    <td className="ds-am" style={{ fontWeight: 600 }}>{formatNumber(prix)}</td>
                    <td className="ds-am" style={{ color: 'var(--p)' }}>{formatNumber(acompte)}</td>
                    <td className="ds-am">
                      <div style={{ width: 50, height: 4, background: 'var(--bg-deep)', borderRadius: 99, overflow: 'hidden', margin: '0 0 0 auto' }}>
                        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: pct >= 100 ? 'var(--success)' : 'var(--p)' }} />
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--tx-3)', marginTop: 2 }}>{pct}%</div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(r.dateReservation)}</td>
                    <td><span className={STATUT_BADGE[r.statut]}>{STATUT_RESERVATION_VEFA_LABELS[r.statut]}</span></td>
                    <td><div style={{ display: 'flex', gap: 4 }}>
                      {r.statut !== 'annulee' && r.statut !== 'acte_authentique' && (
                        <button onClick={() => setAcompteFor(r)} className="ds-btn ds-btn-g ds-btn-sm" title="Encaisser acompte" style={{ color: 'var(--success)' }}>
                          <Banknote size={11} />
                        </button>
                      )}
                      <button onClick={() => onRemove(r.id, r.reference)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ReservationFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <AcompteDialog reservation={acompteFor} onClose={() => setAcompteFor(null)} />
    </>
  );
};

// ── Dialog création réservation ────────────────────────

const ReservationFormDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [lotId, setLotId]         = useState('');
  const [clientId, setClientId]   = useState('');
  const [prix, setPrix]           = useState('');
  const [acompte, setAcompte]     = useState('');
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState<string | null>(null);

  const { data: lotsData }     = useLotsImmo({ statut: 'disponible', limit: 500 });
  const { data: contactsData } = useContacts({ contactType: 'client', limit: 500 });
  const lots = lotsData?.data ?? [];
  const clients = contactsData?.data ?? [];
  const create = useCreateReservationVefa();

  useEffect(() => {
    if (open) { setLotId(''); setClientId(''); setPrix(''); setAcompte(''); setNotes(''); setError(null); }
  }, [open]);

  // Auto-rempli prix avec celui du lot sélectionné
  useEffect(() => {
    if (lotId) {
      const l = lots.find((x) => x.id === lotId);
      if (l) setPrix(String(Number(BigInt(l.prixVenteCents)) / 100));
    }
  }, [lotId, lots]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        lotId, clientId: clientId || undefined,
        prixVenteCents:     Math.round(parseFloat(prix || '0') * 100),
        acompteCumuleCents: acompte ? Math.round(parseFloat(acompte) * 100) : undefined,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = lotId && parseFloat(prix || '0') > 0;

  return (
    <Dialog open={open} onClose={onClose} title="Nouvelle réservation VEFA" subtitle="Réf auto RES-YYYY-NNN · Le lot sélectionné passera auto en 'réservé'" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <Field label="Lot disponible *">
          <select value={lotId} onChange={(e) => setLotId(e.target.value)} className="ds-fi" required style={{ width: '100%' }}>
            <option value="">— Sélectionner —</option>
            {lots.map((l) => (
              <option key={l.id} value={l.id}>{l.code} · {l.designation ?? l.typeBien} · {formatMontant(l.prixVenteCents)} FCFA</option>
            ))}
          </select>
          {lots.length === 0 && <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 4 }}>⚠ Aucun lot disponible — créez-en un d'abord ou libérez une réservation existante.</div>}
        </Field>

        <Field label="Client (acquéreur)">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
            <option value="">— À renseigner plus tard —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.companyName ?? c.fullName}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Prix vente négocié (FCFA) *"><input type="number" min="0" step="1" value={prix} onChange={(e) => setPrix(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="85000000" /></Field>
          <Field label="Acompte initial (FCFA)"><input type="number" min="0" step="1" value={acompte} onChange={(e) => setAcompte(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="0" /></Field>
        </div>

        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="Conditions, échéancier..." /></Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending}>{create.isPending ? 'Création…' : 'Créer la réservation'}</button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Dialog encaissement acompte ──────────────────────

const AcompteDialog = ({ reservation, onClose }: { reservation: ReservationVefa | null; onClose: () => void }) => {
  const open = reservation !== null;
  const [montant, setMontant] = useState('');
  const [note, setNote]       = useState('');
  const [error, setError]     = useState<string | null>(null);
  const ajouter = useAjouterAcompte();

  useEffect(() => { if (open) { setMontant(''); setNote(''); setError(null); } }, [open]);

  if (!reservation) return null;

  const prix    = Number(BigInt(reservation.prixVenteCents)) / 100;
  const acompte = Number(BigInt(reservation.acompteCumuleCents)) / 100;
  const reste   = prix - acompte;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await ajouter.mutateAsync({
        id: reservation.id,
        payload: { montantCents: Math.round(parseFloat(montant || '0') * 100), note: note.trim() || undefined },
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = parseFloat(montant || '0') > 0;

  return (
    <Dialog open={open} onClose={onClose} title={`Encaisser un acompte — ${reservation.reference}`} subtitle={`Prix ${formatNumber(prix)} FCFA · Déjà payé ${formatNumber(acompte)} FCFA · Reste ${formatNumber(reste)} FCFA`} size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <Field label="Montant acompte (FCFA) *">
          <input type="number" min="1" step="1" value={montant} onChange={(e) => setMontant(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder={String(Math.round(reste / 4))} />
        </Field>

        <Field label="Note sur ce versement">
          <input value={note} onChange={(e) => setNote(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="ex: 'Chèque BICICI n°XXX'" />
        </Field>

        <div style={{ fontSize: 11, color: 'var(--tx-3)', padding: 8, background: 'var(--surface)', borderRadius: 6 }}>
          ℹ Si l'acompte cumulé atteint le prix vente, statut auto → "Acompte complet" (signature acte notarié possible).
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || ajouter.isPending}>
            <Banknote size={14} /> {ajouter.isPending ? 'Enregistrement…' : 'Encaisser'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
